import useFeedbackStore from "../store/useFeedbackStore";
import { uploadFile } from "./sparkApi/fileUpload";
import { updateRecaching } from "./sparkApi/updateRecaching";
import { invalidateChatDataCache } from "./sparkApi/getCacheandFileData";
import { FileStore } from "../store/fileUploadStore";
import useHeaderSelectedAIModel from "../store/useHeaderSelectedAIModel";
import { MAX_FILE_COUNT, MAX_TOTAL_SIZE_MB, MAX_SINGLE_FILE_SIZE_MB, ALLOWED_FILE_EXTENSIONS } from "../constants/fileConstants";
import { getFileModelCompatibilityError } from "./fileModelCompatibility";
import { getUploadLimitError } from "./fileUploadValidation";
import logger from "../services/Logger";
import { v4 as uuidv4 } from "uuid";

export const handleFileUpload = (
  e,
  chat_id,
  fileInputRef,
  setFiles,
  files,
  user,
  orgId,
  messageLength,
  isNewChat,
  space,
  agent
) => {
  try {
    const selectedModel = useHeaderSelectedAIModel.getState().selectedModel;
    if (messageLength > 0) {
      updateRecaching(chat_id, true);
      invalidateChatDataCache(chat_id);
    }
    e?.preventDefault?.();

  // Get all selected files
  const selectedFiles = Array.from(fileInputRef?.current?.files || []);
  if (selectedFiles.length === 0) return;

  const isActuallyNewChat = isNewChat;
  const existingFiles = isActuallyNewChat
    ? FileStore.getState()?.new_chat_files || []
    : files;

  const validFiles = [];
  const errors = [];

  for (const file of selectedFiles) {
    const fileExtension = (file.name || "").split(".").pop().toLowerCase();
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    const isAudio = file.type.startsWith("audio/");

    const compatibilityError = getFileModelCompatibilityError(file, selectedModel);
    if (compatibilityError) {
      errors.push(`${file.name}: ${compatibilityError}`);
      continue;
    }

    // Allowed extensions — source of truth is fileConstants.js
    const filteredAllowedExtensions = agent?.name
      ? ALLOWED_FILE_EXTENSIONS.filter((ext) => !["heic", "heif"].includes(ext))
      : ALLOWED_FILE_EXTENSIONS;

    if (
      !filteredAllowedExtensions.includes(fileExtension) &&
      !isImage &&
      !isVideo &&
      !isAudio
    ) {
      const allowedTypes = ALLOWED_FILE_EXTENSIONS.join(", ") + ", images, video, audio";
      errors.push(
        `${file.name}: Invalid file type! Allowed types: ${allowedTypes}.`
      );
      continue;
    }

    const limitError = getUploadLimitError(existingFiles, [...validFiles, file], {
      maxFileCount: MAX_FILE_COUNT,
      maxSingleFileSizeMB: MAX_SINGLE_FILE_SIZE_MB,
      maxTotalSizeMB: MAX_TOTAL_SIZE_MB,
    });
    if (limitError) {
      errors.push(`${file.name}: ${limitError}`);
      continue;
    }

    validFiles.push(file);
  }

  if (errors.length > 0) {
    errors.forEach((error) => useFeedbackStore.getState().showFeedback(error, "error"));
  }

  if (validFiles.length > 0) {
    // If a custom setter is provided (e.g. edit box), always use uploadFile
    // so files never land in the global new_chat_files store
    const isSetFilesOverride = setFiles !== FileStore.getState().setFiles;
    if (messageLength > 0 || !isActuallyNewChat || isSetFilesOverride) {
      validFiles.forEach((file) => {
        const file__path = `${Date.now()}_${file.name}`;
        uploadFile(
          chat_id,
          fileInputRef,
          file__path,
          space,
          file,
          setFiles,
          uuidv4()
        );
      });
    } else {
      const setNewFiles = FileStore.getState().setNewFiles;
      const newFilesArray = validFiles.map((file) => {
        const file__path = `${Date.now()}_${file.name}`;
        return { file, file__path, upload_id: uuidv4() };
      });

      const currentNewFiles = FileStore.getState().new_chat_files || [];
      setNewFiles([...currentNewFiles, ...newFilesArray]);
    }
  }
  } catch (error) {
    logger.error('fileUploadHandler', 'Error in handleFileUpload', { error: error.message });
    useFeedbackStore.getState().showFeedback('An error occurred during file upload. Please try again.', "error");
  }
};
